import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDateTime } from "../lib/api";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import EmptyState from "../components/EmptyState";
import { TrendingUp, TrendingDown, ShoppingCart, Edit3, UserCheck, History } from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useRoleLabel as useRoleLabelAuth } from "../lib/auth";

const SOURCE_KEYS = {
  po_override: { icon: ShoppingCart, key: "audit.sourcePoOverride", chip: "bg-amber-100 text-amber-800 border-amber-200" },
  corporate_edit: { icon: Edit3, key: "audit.sourceCorpEdit", chip: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  supplier_proposal: { icon: UserCheck, key: "audit.sourceSupplierProposal", chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
};

export default function PriceAuditLog() {
  const { t } = useTranslation();
  const roleLabel = useRoleLabelAuth();
  const [logs, setLogs] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/price-audit-log").then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, []);

  if (logs === null) return <><Topbar title={t("audit.title")} /><PizzaSpinner /></>;

  const filtered = filter === "all" ? logs : logs.filter((l) => l.source === filter);

  return (
    <>
      <Topbar title={t("audit.title")} subtitle={t("audit.subtitle")} />
      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button onClick={() => setFilter("all")} data-testid="audit-filter-all"
            className={`bg-white rounded-2xl border p-4 text-left transition ${filter === "all" ? "border-[#1A1A1A] ring-2 ring-[#FFDF00]" : "border-slate-200"}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("audit.total")}</div>
            <div className="text-2xl font-display font-extrabold mt-1">{logs.length}</div>
            <div className="text-[11px] text-slate-500 mt-1">{t("audit.totalSub")}</div>
          </button>
          {Object.entries(SOURCE_KEYS).map(([k, m]) => {
            const c = logs.filter((l) => l.source === k).length;
            return (
              <button key={k} onClick={() => setFilter(k)} data-testid={`audit-filter-${k}`}
                className={`bg-white rounded-2xl border p-4 text-left transition ${filter === k ? "border-[#1A1A1A] ring-2 ring-[#FFDF00]" : "border-slate-200"}`}>
                <div className="flex items-center gap-1.5">
                  <m.icon className="w-3.5 h-3.5 text-slate-500" />
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t(m.key).split("(")[0].trim()}</div>
                </div>
                <div className="text-2xl font-display font-extrabold mt-1">{c}</div>
              </button>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <EmptyState icon={History} title={t("audit.emptyTitle")} message={t("audit.emptyMsg")} />
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colDate")}</th>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colSupply")}</th>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colSupplier")}</th>
                  <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colFrom")}</th>
                  <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colTo")}</th>
                  <th className="text-right px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colPct")}</th>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colSource")}</th>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colUser")}</th>
                  <th className="text-left px-5 py-3 text-[11px] font-bold text-slate-500 uppercase tracking-wider">{t("audit.colReason")}</th>
                </tr>
              </thead>
              <tbody data-testid="audit-log-tbody">
                {filtered.map((row, i) => {
                  const meta = SOURCE_KEYS[row.source] || { icon: History, key: "audit.sourcePoOverride", chip: "bg-slate-100 text-slate-700 border-slate-200" };
                  const Icon = meta.icon;
                  const goingUp = row.change_percent >= 0;
                  return (
                    <motion.tr key={row.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
                      className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`audit-row-${i}`}>
                      <td className="px-5 py-3 text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                      <td className="px-5 py-3 text-sm font-semibold">{row.supply_name}<div className="text-[11px] text-slate-400">{row.supply_unit}</div></td>
                      <td className="px-5 py-3 text-sm">{row.supplier_name}</td>
                      <td className="px-5 py-3 text-sm font-mono text-right text-slate-500">{fmtMXN(row.old_price)}</td>
                      <td className="px-5 py-3 text-sm font-mono text-right font-bold">{fmtMXN(row.new_price)}</td>
                      <td className="px-5 py-3 text-sm text-right">
                        <span className={`inline-flex items-center gap-1 font-bold ${goingUp ? "text-rose-600" : "text-emerald-600"}`}>
                          {goingUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                          {goingUp ? "+" : ""}{row.change_percent}%
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold border ${meta.chip}`}>
                          <Icon className="w-3 h-3" /> {t(meta.key)}
                        </span>
                        {row.po_folio && <div className="text-[10px] text-slate-400 mt-1 font-mono">{row.po_folio}</div>}
                      </td>
                      <td className="px-5 py-3 text-xs">
                        <div className="font-semibold text-[#1A1A1A]">{row.changed_by_name}</div>
                        <div className="text-slate-400 text-[10px]">{roleLabel(row.changed_by_role)}</div>
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-600 max-w-xs">{row.reason}</td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
